import { useRef, useState } from 'react';
import axios from 'axios';
import { ProTableOt, Row, Col, Button, useProTableOt, useCRUDController, Tag, Modal, Spin } from '../../ui/ot'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../ui/table'

const StaffPatronProgressTable = ({
    rowKey = 'library_task_id',
}) => {
    const actionRef = useRef()
    const failureRequestSeqRef = useRef(0)
    const failureAbortRef = useRef(null)
    const [failureModalOpen, setFailureModalOpen] = useState(false)
    const [failureRecord, setFailureRecord] = useState(null)
    const [failureRows, setFailureRows] = useState([])
    const [failureLoading, setFailureLoading] = useState(false)
    const getDisplayText = (text, record) => {
        const finished = text === "匯入成功" || record?.end_time
            || (typeof text === "string" && text.includes("失敗")) || text === "無法匯入";
        if (!finished) return "進行中";
        return text === "匯入成功" && Number(record?.failure_count ?? 0) === 0
            ? "匯入成功" : "無法匯入";
    };
    const hasFailureRows = (record) => Number(record?.failure_count ?? 0) > 0;
    const formatProgress = (record) => {
        const totalCount = record?.total_count;
        const processedCount = Number(record?.processed_count ?? 0);
        const failureCount = Number(record?.failure_count ?? 0);
        const displayText = getDisplayText(record?.task_explaination, record);
        const hasTotalCount = totalCount !== null && totalCount !== undefined && totalCount !== "";

        if (!hasTotalCount) return displayText === "進行中" ? "計算中" : "-";
        if (Number(totalCount) === 0) return "無可處理資料";
        return `${processedCount} / ${totalCount}${failureCount > 0 ? `（失敗：${failureCount}）` : ""}`;
    }
    const parseRowSummary = (rowSummary) => {
        if (!rowSummary) return {};
        if (typeof rowSummary === "object") return rowSummary;
        try {
            return JSON.parse(rowSummary);
        } catch (e) {
            return {};
        }
    }
    const rowSummaryLabels = {
        sheet_name: "工作表",
        staff_name: "姓名",
        gender_id: "性別",
        branchcode: "所屬分館",
        categorycode: "讀者類型",
        dateexpiry: "有效日期",
        missing_fields: "缺漏欄位",
        has_email: "有 Email",
        has_phone: "有行動電話",
    }
    const formatSummaryValue = (value) => {
        if (Array.isArray(value)) return value.join("、");
        if (typeof value === "boolean") return value ? "是" : "否";
        return String(value);
    }
    const renderRowSummary = (rowSummary) => {
        const summary = parseRowSummary(rowSummary);
        const entries = Object.entries(summary).filter(([key, value]) => {
            if (["staff_english_name", "custom_line_id"].includes(key)) return false;
            if (value === null || value === undefined || value === "") return false;
            if (Array.isArray(value) && value.length === 0) return false;
            return true;
        });
        if (entries.length === 0) return "-";
        return <div style={{ display: "grid", gap: 4 }}>
            {entries.map(([key, value]) => (
                <div key={key}>
                    <strong>{rowSummaryLabels[key] ?? key}：</strong>{formatSummaryValue(value)}
                </div>
            ))}
        </div>
    }
    const showFailureDetails = (record) => {
        const requestSeq = failureRequestSeqRef.current + 1;
        failureRequestSeqRef.current = requestSeq;
        failureAbortRef.current?.abort();
        failureAbortRef.current = new AbortController();
        setFailureRecord(record);
        setFailureModalOpen(true);
        setFailureRows([]);
        setFailureLoading(true);
        axios.get("/library/organization_structure/api/staff/concatenation_import/progress/failures", {
            params: {
                library_task_id: record?.library_task_id,
            },
            signal: failureAbortRef.current.signal,
        }).then((response) => {
            if (failureRequestSeqRef.current !== requestSeq) return;
            setFailureRows(response.data?.data ?? []);
        }).catch((error) => {
            if (axios.isCancel?.(error) || error.name === "CanceledError") return;
            if (failureRequestSeqRef.current === requestSeq) setFailureRows([]);
        }).finally(() => {
            if (failureRequestSeqRef.current === requestSeq) setFailureLoading(false);
        });
    }
    const columns = [
        {
            title: '匯入結果',
            dataIndex: 'task_explaination',
            key: 'task_explaination',
            render: (text, record, index) => {
                const displayText = getDisplayText(text, record);

                if (displayText === "無法匯入") {
                    return <Row justify="center">
                        <Col>
                            <Tag color="error"> {displayText}</Tag>
                        </Col>
                    </Row>
                }
                else if (displayText === "進行中") {
                    return <Row justify="center">
                        <Col>
                            <Tag color="blue">{displayText} </Tag>
                        </Col>
                    </Row>
                }
                else {
                    return <Row justify="center">
                        <Col> <Tag color="success">{displayText} </Tag></Col>
                    </Row>

                }

            }
        },
        {
            title: '匯入檔案名稱',
            dataIndex: 'task_name',
            key: 'task_name',
        },
        {
            title: '處理進度',
            dataIndex: 'processed_count',
            key: 'processed_count',
            render: (text, record) => formatProgress(record),
        },
        {
            title: '匯入時間',
            dataIndex: 'start_time',
            key: 'start_time',
        },
        {
            title: '完成時間',
            dataIndex: 'end_time',
            key: 'end_time',
        },
        {
            title: '操作',
            key: 'action',
            render: (text, record) => {
                const displayText = getDisplayText(record?.task_explaination, record);
                if (displayText !== "無法匯入" || !hasFailureRows(record)) return "-";
                return <Button onClick={() => showFailureDetails(record)}>查看失敗</Button>
            },
        },
    ];
    const concatenationImportProgressController = useCRUDController({ apiRoute: "/library/organization_structure/api/staff/concatenation_import/progress" })
    const setParamsFilter = (obj) => {
        return Object.entries(obj).reduce((all, [key, value]) => value === "" ? { ...all } : { ...all, [key]: value }, {})
    }
    const {
        check,
        setCheck,
        setOrders,
        custom_filter_key,
    } = useProTableOt({
        columns
    })

    return <>
        <ProTableOt
            actionRef={actionRef}
            columns={columns}
            check={check}
            setCheck={setCheck}
            rowKey={rowKey}
            request={async (params = {}, sort, filter) => {
                // setAxiosMode("get")
                let newParams = {

                };
                let order = setOrders(sort)
                newParams = {
                    ...params, //只有這個範例要有，其他請看你的後端要什麼資料
                    order,
                    size: params.pageSize,
                    cur_page: params.current,
                    custom_filter_value: params.keyword ?? "",
                    custom_filter_key: custom_filter_key,
                };
                newParams = setParamsFilter({
                    ...newParams,
                });
                // setPageSize(params?.pageSize ?? 20)
                return concatenationImportProgressController.getData({
                    ...newParams
                })
            }}
            rowSelection={false}
        />
        <Modal
            title={`無法匯入明細${failureRecord?.task_name ? ` - ${failureRecord.task_name}` : ""}`}
            open={failureModalOpen}
            onClose={() => {
                failureRequestSeqRef.current += 1;
                failureAbortRef.current?.abort();
                setFailureLoading(false);
                setFailureModalOpen(false);
            }}
            close
            width="90vw"
            contentStyle={{ padding: 12 }}
        >
            <Spin spinning={failureLoading}>
                {failureRows.length === 0 ? (
                    <div style={{ padding: 16, textAlign: "center" }}>無失敗明細</div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Row</TableHead>
                                <TableHead>Sheet</TableHead>
                                <TableHead>識別欄位</TableHead>
                                <TableHead>錯誤訊息</TableHead>
                                <TableHead>摘要</TableHead>
                                <TableHead>建立時間</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {failureRows.map((item) => (
                                <TableRow key={item.staff_patron_import_failure_id}>
                                    <TableCell>{item.row_number}</TableCell>
                                    <TableCell>{item.sheet_name ?? "-"}</TableCell>
                                    <TableCell>{item.staff_serial_name ?? "-"}</TableCell>
                                    <TableCell>{item.error_message}</TableCell>
                                    <TableCell>{renderRowSummary(item.row_summary)}</TableCell>
                                    <TableCell>{item.create_time ?? "-"}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Spin>
        </Modal>
    </>
}
export default StaffPatronProgressTable
